import { useEffect, useState } from "react";
import type { Row } from "../types";
import { api, ApiError, type SugestaoTreino } from "../api";
import { IconClose, IconSparkles, IconCheck } from "../icons";

interface Props {
  colaborador: Row; // colaborador alvo
  onClose: () => void;
  onApplied: () => void; // recarrega listas após criar os treinamentos
}

/**
 * Modal de IA: sugere quais treinamentos o colaborador precisa realizar
 * (com base em cargo, setor e competências) e permite criá-los como "Planejado".
 */
export default function SugerirTreinosModal({ colaborador, onClose, onApplied }: Props) {
  const [data, setData] = useState<SugestaoTreino | null>(null);
  const [marcados, setMarcados] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  async function gerar() {
    setLoading(true);
    setError(null);
    setOkMsg(null);
    try {
      const res = await api.rhIa.sugerirTreinamentos(colaborador.id);
      setData(res);
      // todos marcados por padrão
      const init: Record<number, boolean> = {};
      res.treinamentos.forEach((_, i) => (init[i] = true));
      setMarcados(init);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao gerar sugestões");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    gerar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colaborador.id]);

  const selecionados =
    data?.treinamentos.filter((_, i) => marcados[i]) ?? [];

  async function aplicar() {
    if (selecionados.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.rhIa.aplicarTreinamentos(colaborador.id, selecionados);
      setOkMsg(`${res.criados} treinamento(s) criado(s) como "Planejado" na aba Treinamentos.`);
      onApplied();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao criar treinamentos");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-ia" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            <IconSparkles size={18} /> Treinamentos sugeridos — {colaborador.nome}
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="empty">Analisando o perfil e gerando sugestões…</div>
          ) : error && !data ? (
            <div className="form-error">{error}</div>
          ) : data ? (
            <>
              <div className="ia-diag">
                <span className={`ia-tag ${data.fonte === "ia" ? "ia-tag-ai" : "ia-tag-std"}`}>
                  {data.fonte === "ia" ? "IA" : "Padrão"}
                </span>
                {data.diagnostico || "Treinamentos recomendados para o colaborador."}
              </div>

              <ul className="ia-list">
                {data.treinamentos.map((t, i) => (
                  <li key={i} className="ia-item">
                    <label className="ia-check">
                      <input
                        type="checkbox"
                        checked={!!marcados[i]}
                        onChange={(e) =>
                          setMarcados((m) => ({ ...m, [i]: e.target.checked }))
                        }
                      />
                      <span className="ia-item-main">
                        <span className="ia-item-title">
                          {t.nome_treinamento}
                          <span className={`prio prio-${t.prioridade.toLowerCase()}`}>
                            {t.prioridade}
                          </span>
                          <span className="ia-carga">{t.carga_horaria}h</span>
                        </span>
                        {t.justificativa && (
                          <span className="ia-item-sub">{t.justificativa}</span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>

              {error && <div className="form-error">{error}</div>}
              {okMsg && (
                <div className="form-ok">
                  <IconCheck size={16} /> {okMsg}
                </div>
              )}
            </>
          ) : null}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={gerar} disabled={loading || saving}>
            Regerar
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={aplicar}
            disabled={loading || saving || selecionados.length === 0}
          >
            {saving ? "Criando…" : `Criar selecionados (${selecionados.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
